/**
 * RelicsTab — renders the Relics list from a `JournalTabState`.
 *
 * Thin Phaser renderer mirroring `WhispersTab`. The snapshot -> tab-state
 * decision lives in `journalDispatch.buildJournalTabState('relics', snapshot)`.
 * Each Relic renders as a name heading + narrative blurb body + tier footnote.
 *
 * Per `relics-seed.md` §0.2, the journal spread does NOT display mechanical
 * effect text — only flavor prose. The `effect` field is intentionally
 * excluded from the render.
 */

import * as Phaser from 'phaser';
import {
  JOURNAL_FONT,
  JOURNAL_INK,
  JOURNAL_INK_MUTED,
  JOURNAL_FONT_SIZE,
  JOURNAL_LAYOUT,
} from './journalTheme';
import type { JournalTabState } from './journalDispatch';

/** Render the Relics tab into `container`. Container is cleared before
 *  the new lines are laid out. */
export function renderRelicsTab(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  state: JournalTabState,
): void {
  container.removeAll(true);
  if (state.lines.length === 0) {
    appendEmptyState(scene, container, state.emptyMessage);
    return;
  }

  let y = 0;
  for (const line of state.lines) {
    const heading = scene.add.text(0, y, line.heading ?? '', {
      fontFamily: JOURNAL_FONT,
      fontSize: JOURNAL_FONT_SIZE.HEADING,
      color: JOURNAL_INK,
      fontStyle: 'bold',
    });
    container.add(heading);
    y += 22;

    const body = scene.add.text(0, y, line.body, {
      fontFamily: JOURNAL_FONT,
      fontSize: JOURNAL_FONT_SIZE.BODY,
      color: JOURNAL_INK_MUTED,
      fontStyle: 'italic',
      wordWrap: { width: JOURNAL_LAYOUT.CONTENT_WIDTH - JOURNAL_LAYOUT.CONTENT_PAD_X * 2 },
    });
    container.add(body);
    y += body.height + 6;

    if (line.footnote) {
      const footnote = scene.add.text(0, y, line.footnote, {
        fontFamily: JOURNAL_FONT,
        fontSize: JOURNAL_FONT_SIZE.FOOTNOTE,
        color: JOURNAL_INK_MUTED,
      });
      container.add(footnote);
      y += 16;
    }
    y += JOURNAL_LAYOUT.LINE_SPACING;
  }
}

function appendEmptyState(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  message: string,
): void {
  const text = scene.add.text(0, 24, message, {
    fontFamily: JOURNAL_FONT,
    fontSize: JOURNAL_FONT_SIZE.EMPTY_STATE,
    color: JOURNAL_INK_MUTED,
    fontStyle: 'italic',
    wordWrap: { width: JOURNAL_LAYOUT.CONTENT_WIDTH - JOURNAL_LAYOUT.CONTENT_PAD_X * 2 },
  });
  container.add(text);
}
